import JSZip from "jszip"
import { isEmpty } from "lodash"

import { colors } from "@/components/colors"
import { EXPORT_TYPE } from "@/app/report/common/type"
import { getDateTimeString } from "@/app/report/common/tools"
import { ChartStyle as ChartStyleMultiple } from "@/app/report/common/chart/multiple"
import { PDF } from "@/app/report/common/export/pdf"

/*
    범례 요소
 */
export interface Legend {
  text: string
  color: string
  left: number
}

/*
    범례 스타일
 */
class StyleLegend {
  magnification: number
  fontSize: number
  fontFamily: string
  colorLegendText: string
  circleRadius: number
  marginTop: number
  height: number
  spaceBetweenCircleAndText: number

  constructor(magnification = 1.0) {
    this.magnification = magnification
    this.fontSize = 14 * magnification
    this.fontFamily = "pretendard"
    this.colorLegendText = colors.neutral.grey["1000"]
    this.circleRadius = 7 * magnification
    this.marginTop = 24 * magnification
    this.height = 40 * magnification
    this.spaceBetweenCircleAndText = 8 * magnification
  }
}

/*
    SVG를 Canvas로 변환
 */
async function convertSvgToCanvas(
  svg: SVGSVGElement,
  colorBackground: string | null = "white",
): Promise<HTMLCanvasElement | null> {
  const xml = new XMLSerializer().serializeToString(svg)
  const xmlEncoded = encodeURIComponent(xml)
  const image64 = "data:image/svg+xml;utf8," + xmlEncoded

  const image = document.createElement("img") as HTMLImageElement
  image.src = image64
  await image.decode()

  const { canvas, context } = createCanvas(
    svg.viewBox.baseVal.width,
    svg.viewBox.baseVal.height,
    colorBackground,
  )

  if (!canvas || !context) {
    return null
  }

  context.drawImage(image, 0, 0)

  return canvas
}

/*
    다운로드 관련 변수
 */
const download = {
  arrayCanvasForDownload: [] as HTMLCanvasElement[],
  arrayFileNameForDownload: [] as string[],
  flagDownloading: false,
  timerIntervalId: 0,
  timerInterval: 200,
}

/*
    캔버스 다운로드
    동시 다운로드에 대한 브라우저의 차단을 막기 위해
    시간차를 두고 개별 파일에 대한 다운로드를 실행
 */
export function downloadCanvas(canvas: HTMLCanvasElement, fileName: string) {
  download.arrayCanvasForDownload.push(canvas)
  download.arrayFileNameForDownload.push(fileName)

  if (!download.flagDownloading) {
    download.flagDownloading = true

    download.timerIntervalId = window.setInterval(() => {
      if (download.arrayCanvasForDownload.length === 0) {
        download.flagDownloading = false
        window.clearInterval(download.timerIntervalId)
      } else {
        const canvasForDownload = download.arrayCanvasForDownload.shift()
        const fileNameForDownload = download.arrayFileNameForDownload.shift()

        if (canvasForDownload && fileNameForDownload) {
          const link = document.createElement("a")
          const uri = canvasForDownload
            .toDataURL("image/png")
            .replace("image/png", "image/octet-stream")
          link.setAttribute("download", fileNameForDownload)
          link.setAttribute("href", uri)
          link.click()
        }
      }
    }, download.timerInterval)
  }
}

/*
    캔버스 생성
 */
function createCanvas(
  width: number,
  height: number,
  colorBackground: string | null = "white",
): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D | null } {
  const canvas = document.createElement("canvas") as HTMLCanvasElement
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")

  if (canvas && context) {
    // 배경색 채우기
    if (colorBackground) {
      context.fillStyle = colorBackground
      context.fillRect(0, 0, canvas.width, canvas.height)
    }
  }

  return { canvas, context }
}

/*
    범례 추가
 */
function drawLegend(
  context: CanvasRenderingContext2D,
  legends: Legend[],
  paddingLeft: number,
  paddingTop: number,
  magnification = 1,
) {
  legends.forEach((legend) => {
    const styleLegend = new StyleLegend(magnification)

    const top = styleLegend.marginTop + paddingTop

    const leftCircle = legend.left * magnification + paddingLeft
    context.font = `${styleLegend.fontSize}px ${styleLegend.fontFamily}`
    context.fillStyle = legend.color
    context.fillText("●", leftCircle, top)

    const leftText =
      leftCircle + styleLegend.circleRadius * 2 + styleLegend.spaceBetweenCircleAndText
    context.fillStyle = styleLegend.colorLegendText
    context.fillText(legend.text ?? "", leftText, top)
  })
}

/*
    scalable.ts에서 작성한 차트를 이미지로 저장하거나 pdf에 추가
 */
export async function exportChartWithLegend(
  chart: SVGSVGElement,
  legends: Legend[],
  fileName: string,
  colorBackground: string | null,
  exportType: EXPORT_TYPE,
  pdf: PDF | null = null,
) {
  const styleLegend = new StyleLegend()
  const padding = { top: 20, right: 20, bottom: 20, left: 20 }
  const canvasChart = await convertSvgToCanvas(chart)
  const canvasWidth = chart.viewBox.baseVal.width + padding.left + padding.right
  const canvasHeight =
    chart.viewBox.baseVal.height + padding.top + padding.bottom + styleLegend.height
  const { canvas, context } = createCanvas(canvasWidth, canvasHeight, colorBackground)

  if (!canvas || !context || !canvasChart) {
    return
  }

  // Legend 삽입
  drawLegend(context, legends, padding.left, padding.top)

  // 차트 이미지 삽입
  context.drawImage(canvasChart, padding.left, styleLegend.height + padding.top)

  switch (exportType) {
    case EXPORT_TYPE.PNG:
      {
        // 다운로드
        downloadCanvas(canvas, `${fileName}.png`)
      }
      break

    case EXPORT_TYPE.PDF:
      {
        pdf?.appendCanvas(canvas)
      }
      break
  }
}

/*
    multiple.ts에서 작성한 차트를 이미지로 저장하거나 pdf에 추가
    차트 하나당 하나의 이미치로 저장
 */
export async function exportPartialChartWithLegend(
  svgChart: SVGSVGElement,
  svgYAxisLeft: SVGSVGElement,
  svgYAxisRight: SVGSVGElement | null,
  legends: Legend[],
  fileName: string,
  colorBackground: string | null,
  exportType: EXPORT_TYPE,
  pdf: PDF | null = null,
) {
  const styleLegend = new StyleLegend()
  const canvasWidthMin = 800
  const padding = { top: 20, right: 20, bottom: 20, left: 20 }
  const widthYAxisLeft = svgYAxisLeft.viewBox.baseVal.width
  const widthYAxisRight = svgYAxisRight ? svgYAxisRight.viewBox.baseVal.width : 0
  const widthChart = svgChart.viewBox.baseVal.width
  const heightChart = svgChart.viewBox.baseVal.height
  const canvasWidth =
    Math.max(widthYAxisLeft + widthChart + widthYAxisRight, canvasWidthMin) +
    padding.left +
    padding.right
  const canvasHeight = heightChart + styleLegend.height + padding.top + padding.bottom

  const canvasChart = await convertSvgToCanvas(svgChart)
  const canvasYAxisLeft = await convertSvgToCanvas(svgYAxisLeft)
  const canvasYAxisRight = svgYAxisRight ? await convertSvgToCanvas(svgYAxisRight) : null

  const { canvas, context } = createCanvas(canvasWidth, canvasHeight, colorBackground)

  if (!canvas || !context || !canvasChart || !canvasYAxisLeft) {
    return
  }

  // Legend 삽입
  drawLegend(context, legends, padding.left, padding.top)

  // Y Axis Left 이미지 삽입
  context.drawImage(
    canvasYAxisLeft,
    padding.left,
    styleLegend.height + padding.top,
    widthYAxisLeft,
    heightChart,
  )

  // 차트 삽입
  context.drawImage(
    canvasChart,
    widthYAxisLeft + padding.left,
    styleLegend.height + padding.top,
    widthChart,
    heightChart,
  )

  // Y Axis Right 이미지 삽입
  if (canvasYAxisRight) {
    context.drawImage(
      canvasYAxisRight,
      widthYAxisLeft + widthChart + padding.left,
      styleLegend.height + padding.top,
      widthYAxisRight,
      heightChart,
    )
  }

  switch (exportType) {
    case EXPORT_TYPE.PNG:
      {
        // 다운로드
        downloadCanvas(canvas, `${fileName}.png`)
      }
      break

    case EXPORT_TYPE.PDF:
      {
        pdf?.appendCanvas(canvas)
      }
      break
  }
}

export interface CanvasAndFileName {
  canvas: HTMLCanvasElement
  fileName: string
}

/*
    multiple.ts에서 작성한 차트를 여러 이미지로 분할하여 저장하거나 pdf에 추가
    차트 하나의 폭이 너무 길어서 한 이미지로 저장할 수 없을 때 여러 개의 이미지로 분할하여 저장
 */
export async function exportPartialChartWithLegendToSplitted(
  svgChart: SVGSVGElement,
  svgYAxisLeft: SVGSVGElement,
  svgYAxisRight: SVGSVGElement | null,
  legends: Legend[],
  fileName: string,
  chartStyle: ChartStyleMultiple,
  colorBackground: string | null,
  exportType: EXPORT_TYPE,
  flagFillEmptySpace: boolean,
  pdf: PDF | null = null,
  countOfXAxisItem = 0,
  isIntervalChart = false,
): Promise<CanvasAndFileName[] | null> {
  const widthMin = chartStyle.size.width * 0.8
  const widthMax = chartStyle.size.width
  const padding = {
    top: 20 * chartStyle.magnification,
    right: 20 * chartStyle.magnification,
    bottom: 20 * chartStyle.magnification,
    left: 20 * chartStyle.magnification,
  }
  const widthYAxisLeft = svgYAxisLeft.viewBox.baseVal.width
  const widthYAxisRight = svgYAxisRight ? svgYAxisRight.viewBox.baseVal.width : 0
  const widthChart = Math.floor(svgChart.viewBox.baseVal.width)
  const heightChart = Math.floor(svgChart.viewBox.baseVal.height)
  const widthSpaceForChartUsable =
    widthMax - widthYAxisLeft - widthYAxisRight - chartStyle.space.xAxisChartNameWidth // 실제 차트가 그려질 수 있는 폭(Y축과 차트 이름 영역 제외)
  const countOfXAxisItemNow = Math.floor(widthSpaceForChartUsable / chartStyle.widthOfXAxisItem) // X 축에 표시할 최대 아이템 수
  if (countOfXAxisItem === 0) {
    countOfXAxisItem = countOfXAxisItemNow || 1
  }
  const exportChartNameLeft = isIntervalChart ? 8 : 0

  const styleLegend = new StyleLegend(chartStyle.magnification)
  const widthSpaceForChartActual =
    widthYAxisLeft +
    widthYAxisRight +
    chartStyle.space.xAxisChartNameWidth +
    countOfXAxisItem * chartStyle.widthOfXAxisItem
  const canvasWidth = Math.max(widthMin, widthSpaceForChartActual) + padding.left + padding.right
  const canvasHeight = heightChart + styleLegend.height + padding.top + padding.bottom
  const countOfImage = Math.ceil(
    (widthChart - chartStyle.space.xAxisChartNameWidth) /
      Math.floor(countOfXAxisItem * chartStyle.widthOfXAxisItem),
  )

  const canvasChart = await convertSvgToCanvas(svgChart)
  const canvasYAxisLeft = await convertSvgToCanvas(svgYAxisLeft)
  const canvasYAxisRight = svgYAxisRight ? await convertSvgToCanvas(svgYAxisRight) : null

  const imageArray = new Array<CanvasAndFileName>()
  for (let i = 0; i < countOfImage; i++) {
    const { canvas, context } = createCanvas(canvasWidth, canvasHeight, colorBackground)

    if (!canvas || !context || !canvasChart || !canvasYAxisLeft) {
      continue
    }

    // Legend 삽입
    drawLegend(context, legends, padding.left, padding.top, chartStyle.magnification)
    // Y Axis Left 이미지 삽입
    context.drawImage(
      canvasYAxisLeft,
      padding.left,
      styleLegend.height + padding.top,
      widthYAxisLeft,
      heightChart,
    )

    // 차트 삽입
    const chartTargetWidth = countOfXAxisItem * chartStyle.widthOfXAxisItem
    const chartSourceLeft = chartStyle.space.xAxisChartNameWidth + i * chartTargetWidth
    const chartSourceWidthRemain = widthChart - chartSourceLeft
    const chartTargetWidthActual = Math.min(chartTargetWidth, chartSourceWidthRemain)

    if (!isIntervalChart) {
      if (i === 0) {
        const source = {
          left: 0,
          top: 0,
          width: chartTargetWidthActual + chartStyle.space.xAxisChartNameWidth,
          height: heightChart,
        }
        const target = {
          left: widthYAxisLeft + padding.left,
          top: styleLegend.height + padding.top,
          width: chartTargetWidthActual + chartStyle.space.xAxisChartNameWidth,
          height: heightChart,
        }
        context.drawImage(
          canvasChart,
          source.left,
          source.top,
          source.width,
          source.height,
          target.left,
          target.top,
          target.width,
          target.height,
        )
      } else {
        const source = {
          left: chartSourceLeft,
          top: 0,
          width: chartTargetWidthActual + chartStyle.space.xAxisChartNameWidth,
          height: heightChart,
        }
        const target = {
          left: widthYAxisLeft + padding.left,
          top: styleLegend.height + padding.top,
          width: chartTargetWidthActual + chartStyle.space.xAxisChartNameWidth,
          height: heightChart,
        }
        context.drawImage(
          canvasChart,
          source.left,
          source.top,
          source.width,
          source.height,
          target.left,
          target.top,
          target.width,
          target.height,
        )
      }
    } else {
      if (i === 0) {
        const source = {
          left: chartSourceLeft - chartStyle.space.xAxisChartNameWidth - exportChartNameLeft,
          top: 0,
          width: chartTargetWidthActual,
          height: heightChart,
        }
        const target = {
          left: widthYAxisLeft + padding.left - exportChartNameLeft,
          top: styleLegend.height + padding.top,
          width: chartTargetWidthActual,
          height: heightChart,
        }
        context.drawImage(
          canvasChart,
          source.left,
          source.top,
          source.width,
          source.height,
          target.left,
          target.top,
          target.width,
          target.height,
        )
      } else {
        const source = {
          left: i * chartTargetWidth,
          top: 0,
          width: chartTargetWidthActual,
          height: heightChart,
        }
        const target = {
          left:
            widthYAxisLeft +
            chartStyle.space.xAxisChartNameWidth +
            padding.left -
            exportChartNameLeft,
          top: styleLegend.height + padding.top,
          width: chartTargetWidthActual,
          height: heightChart,
        }
        context.drawImage(
          canvasChart,
          source.left,
          source.top,
          source.width,
          source.height,
          target.left,
          target.top,
          target.width,
          target.height,
        )
      }
    }

    // 차트 이름 삽입
    if (chartStyle.space.xAxisChartNameWidth > 0 && isIntervalChart) {
      if (chartStyle.flagShowChartLabelOnXAxis) {
        context.drawImage(
          canvasChart,
          0,
          0,
          chartStyle.space.xAxisChartNameWidth - exportChartNameLeft,
          heightChart,
          widthYAxisLeft + padding.left,
          styleLegend.height + padding.top,
          chartStyle.space.xAxisChartNameWidth - exportChartNameLeft,
          heightChart,
        )
      } else {
        context.drawImage(
          canvasChart,
          0,
          0,
          chartStyle.space.xAxisChartNameWidth - exportChartNameLeft,
          heightChart - chartStyle.space.xAxisLabelHeight,
          widthYAxisLeft + padding.left,
          styleLegend.height + padding.top,
          chartStyle.space.xAxisChartNameWidth - exportChartNameLeft,
          heightChart - chartStyle.space.xAxisLabelHeight,
        )
      }
    }

    // 현재 영역에 포함되지 않는 x축의 레이블 흔적 삭제
    if (chartStyle.flagLongNameOnXAxis && isIntervalChart) {
      // left top
      const ltLeft = widthYAxisLeft + padding.left
      const ltTop =
        styleLegend.height + heightChart + padding.top - chartStyle.space.xAxisLabelHeight
      const ltWidth = chartStyle.space.xAxisChartNameWidth
      const ltHeight = chartStyle.space.xAxisChartNameWidth
      fillPath(
        context,
        new Path2D(
          `M ${ltLeft} ${ltTop} L ${ltLeft + ltWidth} ${ltTop} L ${ltLeft} ${ltTop + ltHeight} Z`,
        ),
        colors.neutral.white,
      )

      // right bottom
      const rbRight =
        padding.left +
        widthYAxisLeft +
        chartTargetWidthActual +
        chartStyle.space.xAxisChartNameWidth
      const rbTop =
        styleLegend.height + heightChart + padding.top - chartStyle.space.xAxisLabelHeight
      const rbWidth = chartStyle.space.xAxisLabelHeight
      const rbHeight = chartStyle.space.xAxisLabelHeight
      fillPath(
        context,
        new Path2D(
          `M ${rbRight} ${rbTop} L ${rbRight} ${rbTop + rbHeight} L ${rbRight - rbWidth} ${rbTop + rbHeight} Z`,
        ),
        colors.neutral.white,
      )
    }

    let yAxisRightLeft =
      padding.left + widthYAxisLeft + chartStyle.space.xAxisChartNameWidth + chartTargetWidthActual

    // 빈 영역 채우기
    if (flagFillEmptySpace && chartStyle.space.xAxisChartNameWidth > 2) {
      const widthOfSource = 2
      context.drawImage(
        canvasChart,
        0,
        0,
        widthOfSource,
        heightChart - chartStyle.space.xAxisLabelHeight,
        yAxisRightLeft - (!isIntervalChart && i !== 0 ? chartStyle.space.xAxisChartNameWidth : 0),
        styleLegend.height + padding.top,
        chartTargetWidth -
          chartTargetWidthActual +
          (!isIntervalChart && i !== 0 ? chartStyle.space.xAxisChartNameWidth : 0),
        heightChart - chartStyle.space.xAxisLabelHeight,
      )

      // 우측 Y Axis 위치 조정
      yAxisRightLeft =
        widthYAxisLeft + chartStyle.space.xAxisChartNameWidth + chartTargetWidth + padding.left
    }

    // Y Axis Right 이미지 삽입
    if (canvasYAxisRight) {
      context.drawImage(
        canvasYAxisRight,
        yAxisRightLeft,
        styleLegend.height + padding.top,
        widthYAxisRight,
        heightChart,
      )
    }

    switch (exportType) {
      case EXPORT_TYPE.PNG:
        {
          let fileNamePNG = `${fileName}`
          if (countOfImage > 1) {
            fileNamePNG += `_${i}`
          }
          imageArray.push({ canvas, fileName: fileNamePNG })
        }
        break

      case EXPORT_TYPE.PDF:
        {
          pdf?.appendCanvas(canvas)
        }
        break
    }
  }

  switch (exportType) {
    case EXPORT_TYPE.PNG: {
      return imageArray
    }
  }

  return null
}

/*
    영역 채우기
 */
function fillPath(context: CanvasRenderingContext2D, path: Path2D, color: string) {
  context.fillStyle = color
  context.fill(path)
}

/*
    SVG를 이미지로 저장하거나 pdf에 추가
 */
export async function exportSVG(
  chart: SVGSVGElement,
  fileName: string,
  colorBackground: string | null,
  exportType: EXPORT_TYPE,
  pdf: PDF | null = null,
) {
  const styleLegend = new StyleLegend()
  const padding = { top: 20, right: 20, bottom: 20, left: 20 }
  const canvasChart = await convertSvgToCanvas(chart, colorBackground)
  const sourceWidth = chart.viewBox.baseVal.width
  const sourceHeight = chart.viewBox.baseVal.height
  const canvasWidth = sourceWidth + padding.left + padding.right
  const canvasHeight = sourceHeight + padding.top + padding.bottom + styleLegend.height

  const { canvas, context } = createCanvas(canvasWidth, canvasHeight, colorBackground)

  if (!canvas || !context || !canvasChart) {
    return
  }

  context.drawImage(
    canvasChart,
    0,
    0,
    sourceWidth,
    sourceHeight,
    padding.left,
    padding.top,
    sourceWidth,
    sourceHeight,
  )

  switch (exportType) {
    case EXPORT_TYPE.PNG:
      {
        // 다운로드
        downloadCanvas(canvas, `${fileName}.png`)
      }
      break

    case EXPORT_TYPE.PDF:
      {
        pdf?.appendCanvas(canvas)
      }
      break
  }
}

// TODO zip 으로 export 하는 경우, 내부 이미지 수정일이 UTC 기준으로 저장됨
export function exportPngZip(
  fileNameZip: string,
  files: Array<CanvasAndFileName | null | undefined>,
) {
  const zip = new JSZip()
  files.forEach((file) => {
    if (!file || !file.fileName || !file.canvas) {
      return
    }
    zip.file(
      `${file.fileName}.png`,
      file.canvas.toDataURL("image/png").replace(/^data:image\/(png|jpg);base64,/, ""),
      {
        base64: true,
      },
    )
  })
  zip.generateAsync({ type: "blob" }).then((content) => {
    content.arrayBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/zip" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = fileNameZip
      a.click()
    })
  })
}

/*
    multiple.ts에서 작성한 차트를 이미지로 저장하거나 pdf파일로 저장
    출력하는 차트가 1개인 경우 PNG 파일로 저장하고, 2개 이상인 경우 ZIP 파일로 저장
 */
export function exportMultipleChart(
  exportType: EXPORT_TYPE,
  results: Array<CanvasAndFileName[] | null>,
  fileNameHeader: string,
  pdf: PDF,
) {
  switch (exportType) {
    case EXPORT_TYPE.PNG:
      {
        const resultFlatted = results.flat().filter((result) => !!result)

        if (isEmpty(resultFlatted)) {
          return
        }

        if (resultFlatted.length === 1) {
          if (!!resultFlatted[0] && !!resultFlatted[0].canvas && !!resultFlatted[0].fileName) {
            {
              downloadCanvas(resultFlatted[0].canvas, `${resultFlatted[0].fileName}.png`)
            }
          }
        } else if (resultFlatted.length > 1) {
          exportPngZip(`${getDateTimeString()}_${fileNameHeader}.zip`, resultFlatted)
        }
      }
      break

    case EXPORT_TYPE.PDF:
      {
        const fileName = `${getDateTimeString()}_${fileNameHeader}.pdf`
        pdf.save(fileName)
      }
      break
  }
}
